import {Phone, Calendar, Palette, Sparkles, Check, MapPin, Award} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import CanonicalTag from '@/components/CanonicalTag'

const ColorChangeWrapSanDiegoPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does a color change wrap cost in San Diego?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Full color change wraps range from $3,500-$6,500 for sedans and coupes, $4,500-$7,500 for SUVs and trucks. Pricing depends on vehicle size, vinyl quality, and finish selection."
          }
        },
        {
          "@type": "Question",
          "name": "How long does a color change wrap last?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Premium color change wraps last 5-7 years with proper care. Durability depends on vinyl quality, sun exposure, and maintenance routine."
          }
        },
        {
          "@type": "Question",
          "name": "Can I return my car to original color?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Color change wraps are fully reversible. Professional removal reveals factory paint in pristine condition, protected under the wrap for years."
          }
        }
      ]
    })
    document.head.appendChild(script)

    document.title = "Color Change Wrap San Diego | Full Vehicle Color Change & Vinyl Wraps"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional color change wrap installation in San Diego. Transform your vehicle with premium vinyl wraps. 500+ colors, matte/gloss/satin finishes. Serving Vista, Carlsbad, Oceanside.')
    }

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/color-change-wrap-san-diego" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Color Change Wrap San Diego - Complete Vehicle Transformation</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Transform your vehicle's entire appearance with professional color change wrap installation in San Diego. Choose from 500+ premium vinyl colors and finishes to create your dream vehicle—from subtle refinement to bold statement. Our expert installers deliver flawless <a href="/car-wrap-san-diego" className="text-cyan-400 hover:underline">full-body wraps</a> that protect your factory paint while giving you unlimited customization possibilities.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving San Diego, Vista, Carlsbad, Oceanside, Encinitas, and all of North County. We specialize in luxury vehicle color changes, Tesla wraps, exotic car transformations, and <a href="/commercial-fleet-wraps" className="text-cyan-400 hover:underline">commercial fleet rebranding</a>. All wraps include manufacturer warranty and professional installation with precision edge work and seamless panel transitions.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free Color Change Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Color Change Wraps */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Choose Color Change Wraps in San Diego</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              Color change wraps offer complete vehicle transformation without the permanence, expense, or downtime of traditional paint. Unlike repainting—which can cost $10,000-$20,000 and take weeks—<a href="/vinyl-wraps" className="text-cyan-400 hover:underline">premium vinyl wraps</a> are completed in 3-5 days at a fraction of the cost. More importantly, wraps are fully reversible, protecting your factory paint and preserving resale value.
            </p>
            <p>
              San Diego's car enthusiast community embraces color change wraps for good reason: unlimited color options, faster turnaround, and the ability to experiment with bold finishes like matte black, satin pearl, or color-shifting chrome—options impossible or impractical with traditional paint. From luxury sedans in <a href="/ppf-rancho-santa-fe" className="text-cyan-400 hover:underline">Rancho Santa Fe</a> to performance vehicles in <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a>, color change wraps deliver stunning results while maintaining your vehicle's original finish underneath.
            </p>
            <p>
              Our color change wraps use premium cast vinyl from 3M, Avery Dennison, and other industry leaders—materials designed specifically for full-vehicle applications with advanced air-release adhesives, UV protection, and conformability for complex curves. Each installation includes complete disassembly of trim, badges, and lighting for seamless edge tucking and factory-quality finish. Combine your color change with <a href="/chrome-delete" className="text-cyan-400 hover:underline">chrome delete</a> and <a href="/window-tinting" className="text-cyan-400 hover:underline">window tinting</a> for a cohesive transformation.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Color Change Wrap Pricing in San Diego</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Sedan / Coupe</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">$3,500 - $6,500</div>
              <p className="text-gray-400 text-sm mb-6">Compact to mid-size vehicles</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All exterior painted surfaces</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Door jambs and edges wrapped</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Premium cast vinyl (5-7 year life)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Manufacturer warranty included</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
              <h3 className="text-2xl font-bold mb-4 text-white">SUV / Crossover</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">$4,500 - $7,500</div>
              <p className="text-gray-400 text-sm mb-6">Mid-size to full-size SUVs</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Complete color transformation</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Complex body lines and curves</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Trim removal and reinstallation</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Factory-quality edge sealing</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Truck / Large SUV</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">$5,500 - $9,000</div>
              <p className="text-gray-400 text-sm mb-6">Full-size trucks and large SUVs</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Maximum surface area coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Bed and tonneau wrap options</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Premium finish selections</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Extended warranty coverage</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400">
            Starting prices shown are for standard vehicles and vinyl types. Exact pricing determined after vehicle inspection. Specialty finishes (chrome, color-flip) and exotic vehicles may cost more. <a href="/car-wrap-san-diego" className="text-cyan-400 hover:underline">Learn more about our wrap services</a>.
          </p>
        </div>
      </section>

      {/* Color & Finish Options */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Color Change Wrap Finishes</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Gloss Color Change</h3>
              <p className="text-gray-300">High-gloss vinyl for traditional factory paint appearance. Deep reflective finish in any color imaginable. Perfect for maintaining premium OEM aesthetic while changing colors.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Palette className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Matte Color Change</h3>
              <p className="text-gray-300">Ultra-popular matte finishes create sophisticated, stealthy appearance. Matte black, gray, military green, and custom colors available. Requires specialized care products.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Satin Color Change</h3>
              <p className="text-gray-300">Premium satin finishes blend matte and gloss characteristics. Subtle sheen with smooth texture. Easier maintenance than matte while maintaining sophisticated appearance.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Chrome & Metallic</h3>
              <p className="text-gray-300">Eye-catching chrome, brushed aluminum, and metallic color changes for maximum impact. Perfect for exotic vehicles and bold personal expression.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Palette className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Color Flip Wraps</h3>
              <p className="text-gray-300">Advanced color-shifting vinyl changes appearance based on viewing angle and lighting. Creates unique, one-of-a-kind looks impossible with traditional paint.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Custom Color Matching</h3>
              <p className="text-gray-300">Pantone color matching for exact brand colors or custom hues. Perfect for fleet vehicles, business branding, or matching specific color samples.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Our Color Change Wrap Process</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Color Selection & Consultation</h3>
                <p className="text-gray-300">In-person consultation at our Vista facility to review physical vinyl samples from our 500+ color library. We'll discuss your vision, show finish options (gloss, matte, satin, chrome), and create digital mockups of your vehicle in your chosen color.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Vehicle Preparation & Disassembly</h3>
                <p className="text-gray-300">Thorough cleaning, degreasing, and paint inspection ensure perfect vinyl adhesion. We carefully remove trim pieces, badges, door handles, and lighting components for seamless edge tucking—the difference between amateur and professional installations.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Precision Vinyl Application</h3>
                <p className="text-gray-300">Expert installers apply premium cast vinyl panel-by-panel using advanced stretching, heating, and squeegee techniques. Complex curves, recessed areas, and body lines receive expert attention. Door jambs, edges, and shut lines are carefully wrapped for complete color transformation.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Post-Heating & Quality Control</h3>
                <p className="text-gray-300">Heat activation ensures long-lasting adhesion and seals edges against moisture. Final quality inspection confirms bubble-free application and perfect edge work. We reassemble all components and provide comprehensive care instructions for maximizing wrap longevity.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Color Change Wrap FAQs</h2>
          <div className="space-y-6">
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does a color change wrap cost in San Diego?</h3>
              <p className="text-gray-300">Full color change wraps range from $3,500-$6,500 for sedans and coupes, $4,500-$7,500 for SUVs and crossovers, and $5,500-$9,000 for trucks and large SUVs. Pricing depends on vehicle size, vinyl quality (standard vs. premium), and finish selection (gloss, matte, satin, chrome). Specialty finishes like color-flip or chrome cost more due to material costs and installation complexity. Starting prices are for standard vehicles; exact pricing determined after vehicle inspection.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does a color change wrap last?</h3>
              <p className="text-gray-300">Premium cast vinyl color change wraps last 5-7 years in San Diego with proper care. Durability depends on vinyl quality (we only use top-tier materials from 3M and Avery Dennison), sun exposure (garaged vehicles last longest), and maintenance routine. Matte finishes require more careful maintenance than gloss finishes but last equally long when properly cared for.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can I return my car to its original color?</h3>
              <p className="text-gray-300">Yes. Color change wraps are fully reversible. Professional removal by trained technicians reveals factory paint in pristine condition—often better than unwrapped areas because the vinyl protects paint from UV rays, oxidation, and environmental damage for years. This makes color change wraps perfect for leased vehicles or anyone wanting flexibility to change colors again in the future.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does color change wrap installation take?</h3>
              <p className="text-gray-300">Full color change wraps take 3-5 days depending on vehicle size and complexity. Sedans: 3-4 days. SUVs and trucks: 4-5 days. Complex vehicles with intricate body lines or extensive disassembly: up to 5-7 days. We never rush installations—proper preparation, panel-by-panel application, and post-heating ensure your wrap lasts the full 5-7 year lifespan.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What's the difference between color change wrap and paint?</h3>
              <p className="text-gray-300">Color change wraps cost 50-70% less than quality paint jobs ($3,500-$9,000 vs. $10,000-$20,000+), are completed in days instead of weeks, are fully reversible, and offer unlimited color options including finishes impossible with paint (matte, satin, chrome, color-flip). Wraps also protect factory paint underneath, preserving resale value. Paint is permanent and requires extensive prep work including sanding and primer.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Do color change wraps damage paint?</h3>
              <p className="text-gray-300">No. Professional-grade vinyl uses automotive-specific adhesive that removes cleanly when properly installed and removed. Wraps actually protect your factory paint from UV damage, oxidation, minor scratches, and environmental contaminants. After removal, wrapped areas are often in better condition than unwrapped surfaces.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can I wash my wrapped vehicle normally?</h3>
              <p className="text-gray-300">Yes. After 7 days curing time, wash as usual using hand washing or touchless automatic washes. Avoid brush car washes and high-pressure spray directly on edges. Use pH-neutral soap and microfiber towels. Matte and satin finishes require specific matte-safe products to maintain their unique appearance and texture.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What colors are most popular for color change wraps?</h3>
              <p className="text-gray-300">Top color choices in San Diego: Matte black (stealthy, sophisticated), Satin black (easier maintenance than matte), Nardo gray (Audi-inspired matte gray), Miami blue (vibrant gloss blue), Frozen white (pearl white matte), Military green (matte olive), and various satin metallics. We also do custom Pantone color matching for exact brand colors or specific hues.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
          <p className="text-gray-300 text-center mb-8">
            Explore our full range of vehicle wrap and customization services:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a href="/car-wrap-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Car Wrap San Diego</h3>
              <p className="text-gray-400 text-sm">Professional vehicle wrap services including partial wraps, full color change, and custom designs</p>
            </a>
            <a href="/colored-ppf-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Colored PPF San Diego</h3>
              <p className="text-gray-400 text-sm">Color change paint protection film that combines a custom color with self-healing rock-chip protection</p>
            </a>
            <a href="/chrome-delete" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Chrome Delete</h3>
              <p className="text-gray-400 text-sm">Remove chrome trim with vinyl wrap for a modern blacked-out appearance</p>
            </a>
            <a href="/vinyl-wraps" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Vinyl Wraps</h3>
              <p className="text-gray-400 text-sm">Complete vinyl wrap services for all vehicle types and customization needs</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Color Change Wrap Installation Serving San Diego County
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Based in Vista, we provide professional color change wrap installation throughout San Diego County and North County.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-gray-300 text-sm">
            <div className="bg-gray-900 p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">North County Coastal</h3>
              <ul className="space-y-1">
                <li>• Carlsbad</li>
                <li>• Oceanside</li>
                <li>• Encinitas</li>
                <li>• Solana Beach</li>
                <li>• Del Mar</li>
              </ul>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">North County Inland</h3>
              <ul className="space-y-1">
                <li>• Vista (Shop Location)</li>
                <li>• Rancho Santa Fe</li>
                <li>• San Marcos</li>
                <li>• Escondido</li>
                <li>• Poway</li>
              </ul>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">Greater San Diego</h3>
              <ul className="space-y-1">
                <li>• San Diego</li>
                <li>• La Jolla</li>
                <li>• Mission Valley</li>
                <li>• Downtown SD</li>
                <li>• All SD County</li>
              </ul>
            </div>
          </div>
          <p className="text-gray-300 text-center mt-8">
            <strong className="text-white">Shop Address:</strong> 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Transform Your Vehicle's Color?</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free color change consultation and explore 500+ vinyl colors and finishes. View physical samples and digital mockups of your vehicle transformation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free Color Change Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default ColorChangeWrapSanDiegoPage
